import { MAX_ROW_LIMIT, fetchRowChunk, runQueries, buildResource } from '../GscTools/query';

/**
 * The requests this report makes, each one a different question about the same scope.
 *
 * All of them carry the scope's own country, device and session rules, and all of them use `auto`
 * aggregation: every request here is keyed or filtered by a page or a query, and such a row can
 * never be labelled whole-property traffic.
 */

/** The scope fields every request on this report shares. */
function common(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
        filters: scope.ruleFilters,
    };
}

/**
 * One chunk of keyed rows (pages or queries) for one period.
 *
 * Current and baseline are fetched by separate calls with independent cursors: Search Console can
 * hand back a full chunk for one window and a short one for the other, and advancing them
 * together would either re-read rows or skip them.
 */
export function fetchKeyedChunk({ credentialsDomainId, scope, period, offset = 0 }) {
    return fetchRowChunk({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period,
        dimension: scope.dimension,
        offset,
        ...common(scope),
    });
}

/**
 * The rows that contribute to one selected row: the queries behind a page, or the pages behind a
 * query.
 *
 * The selected key is sent as an `equals` filter on the row's own dimension, which Search Console
 * compares case-sensitively and verbatim — the exact string it returned is sent back unchanged.
 * Only the current period is asked for: a contributor list is read inside one window, and a
 * second one would double the request budget for a block that makes no claim about change.
 *
 * What comes back is not guaranteed to sum to the selected row's total. Search Console withholds
 * query rows below its privacy thresholds, so the shortfall is information that was withheld, not
 * traffic that does not exist.
 */
export async function fetchContributors({ credentialsDomainId, scope, period, selectedKey }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'contributors',
                resource: buildResource({
                    period,
                    dimensions: [scope.contributorDimension],
                    aggregationType: 'auto',
                    dataState: scope.dataState,
                    searchType: scope.searchType,
                    country: scope.country,
                    device: scope.device,
                    filters: [
                        ...scope.ruleFilters,
                        {
                            dimension: scope.dimension,
                            operator: 'equals',
                            expression: selectedKey,
                        },
                    ],
                }),
            },
        ],
    });

    return results.contributors ?? [];
}

/**
 * The daily series of one selected row, for one period.
 *
 * The row's key becomes an `equals` filter and the dimension becomes the date, so the series is
 * that row's own history under the same scope the table was read with. Each period is a separate
 * call so a baseline that fails leaves the current line drawn.
 */
export async function fetchSelectedRowSeries({ credentialsDomainId, scope, period, selectedKey }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'daily',
                resource: buildResource({
                    period,
                    dimensions: ['date'],
                    aggregationType: 'auto',
                    dataState: scope.dataState,
                    searchType: scope.searchType,
                    country: scope.country,
                    device: scope.device,
                    filters: [
                        ...scope.ruleFilters,
                        {
                            dimension: scope.dimension,
                            operator: 'equals',
                            expression: selectedKey,
                        },
                    ],
                }),
            },
        ],
    });

    return results.daily ?? [];
}
